import React, { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const CATEGORIES = ['checkedIn', 'notCheckedIn', 'total'];
const EMPTY_AXES = { types: [], options: [] };

function sumMatrix(matrix) {
    let sum = 0;
    for (const opts of Object.values(matrix || {})) {
        for (const v of Object.values(opts || {})) {
            sum += v || 0;
        }
    }
    return sum;
}

function BreakdownMatrix({ title, subtitle, total, seated, unseated, axes, activeMode }) {
    const { t } = useTranslation();
    const { types, options } = axes || EMPTY_AXES;
    const empty = types.length === 0 || options.length === 0;
    const data = activeMode === 'seated' ? seated : unseated;
    const seatedCount = sumMatrix(seated);
    const unseatedCount = sumMatrix(unseated);

    return (
        <Paper variant="outlined" sx={{ p: 2, width: '100%', height: '100%' }}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={2} mb={1}>
                <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>
                    {/* Always render the subtitle line (blank placeholder when empty) so
                        every card's header is the same height and the tables below line up. */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {subtitle || ' '}
                    </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {total ?? 0}
                </Typography>
            </Box>
            <Box display="flex" gap={2} mb={1}>
                <Typography variant="caption" color="text.secondary">
                    {t('checkinview.stats.seated')}: <strong>{seatedCount}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {t('checkinview.stats.unseated')}: <strong>{unseatedCount}</strong>
                </Typography>
            </Box>
            {empty ? (
                <Typography align="left" color="text.secondary">
                    —
                </Typography>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('common.option')}</TableCell>
                                {types.map((typeName) => (
                                    <TableCell key={typeName} align="center">
                                        {typeName}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {options.map((optionName) => (
                                <TableRow key={optionName}>
                                    <TableCell>{optionName}</TableCell>
                                    {types.map((typeName) => (
                                        <TableCell key={`${typeName}-${optionName}`} align="center">
                                            {(data || {})[typeName]?.[optionName] || 0}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
}

function CheckinStats({ stats, note, suppressReviewEmail }) {
    const { t } = useTranslation();
    const [seatMode, setSeatMode] = useState('seated');

    const labelByCategory = {
        checkedIn: t('checkinview.stats.checkedIn'),
        notCheckedIn: t('checkinview.stats.notCheckedIn'),
        total: t('checkinview.stats.sold'),
    };

    const subtitleByCategory = {
        total: t('checkinview.stats.soldDescription'),
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, width: '100%' }}>
            <Box display="flex" flexDirection="column" gap={3} width="100%">
                <Tabs
                    value={seatMode}
                    onChange={(_, val) => setSeatMode(val)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab value="seated" label={t('checkinview.stats.seated')} />
                    <Tab value="unseated" label={t('checkinview.stats.unseated')} />
                </Tabs>

                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }}
                    gap={2}
                    width="100%"
                >
                    {CATEGORIES.map((cat) => (
                        <BreakdownMatrix
                            key={cat}
                            title={labelByCategory[cat]}
                            subtitle={subtitleByCategory[cat]}
                            total={stats?.itemTotals?.[cat]}
                            seated={stats?.[cat]?.seated || {}}
                            unseated={stats?.[cat]?.unseated || {}}
                            axes={stats?.axes?.[cat]}
                            activeMode={seatMode}
                        />
                    ))}
                </Box>

                {note && (
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography
                            variant="overline"
                            display="block"
                            color="text.secondary"
                            sx={{ fontWeight: 700, textAlign: 'left' }}
                        >
                            {t('checkinview.stats.note')}
                        </Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                            {note}
                        </Typography>
                    </Box>
                )}
                {suppressReviewEmail && (
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('checkinview.stats.suppressReviewEmail')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}

export default CheckinStats;
